import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faChevronUp, 
    faThumbsUp, 
    faThumbsDown, 
    faComment, 
    faShareAlt, 
    faBookmark,
    faVolumeHigh,
    faVolumeMute
} from '@fortawesome/free-solid-svg-icons';
import { Share } from '@capacitor/share'; // Native sharing matching NewsCard
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { getFirestore, doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { app } from '../firebase-config';
import './WishesCard.css';

const db = getFirestore(app);

const WishesCard = ({ newsItem, isActive, setActiveCommentPostId }) => {
    const [localNewsItem, setLocalNewsItem] = useState(newsItem);
    const { currentUser, firebaseUser } = useAuth();
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const videoRef = useRef(null);

    // Sync with parent updates
    useEffect(() => {
        setLocalNewsItem(newsItem);
    }, [newsItem]);

    // Handle play/pause via Swiper active state
    useEffect(() => {
        if (!localNewsItem.videoUrl || !videoRef.current) return;
        if (isActive) {
            videoRef.current.play().catch(error => {
                console.log("Autoplay prevented:", error);
            });
        } else {
            videoRef.current.pause();
        }
    }, [isActive, localNewsItem.videoUrl]);

    // Automatic Image Slider
    useEffect(() => {
        if (!localNewsItem.videoUrl && localNewsItem.imageUrls && localNewsItem.imageUrls.length > 1) {
            const timer = setInterval(() => {
                setCurrentImageIndex((prev) => (prev + 1) % localNewsItem.imageUrls.length);
            }, 3000);
            return () => clearInterval(timer);
        }
    }, [localNewsItem.imageUrls, localNewsItem.videoUrl]);

    // Memoize reaction states directly from localNewsItem
    const hasLiked = useMemo(() => currentUser && (localNewsItem.likedBy || []).includes(currentUser.uid), [currentUser, localNewsItem.likedBy]);
    const hasDisliked = useMemo(() => currentUser && (localNewsItem.dislikedBy || []).includes(currentUser.uid), [currentUser, localNewsItem.dislikedBy]);
    const hasSaved = useMemo(() => currentUser && (localNewsItem.savedBy || []).includes(currentUser.uid), [currentUser, localNewsItem.savedBy]);

    // Optimistic UI updates for likes and dislikes
    const handleLikeDislike = async (action) => {
        if (!firebaseUser) { alert("Please log in to react."); return; }

        const originalItem = { ...localNewsItem };
        let updatedItem = { ...localNewsItem };
        const userId = firebaseUser.uid;

        // Ensure arrays exist
        updatedItem.likedBy = updatedItem.likedBy || [];
        updatedItem.dislikedBy = updatedItem.dislikedBy || [];

        if (action === 'like') {
            const alreadyLiked = updatedItem.likedBy.includes(userId);
            updatedItem.likedBy = alreadyLiked ? updatedItem.likedBy.filter(id => id !== userId) : [...updatedItem.likedBy, userId];
            updatedItem.dislikedBy = updatedItem.dislikedBy.filter(id => id !== userId);
        } else if (action === 'dislike') {
            const alreadyDisliked = updatedItem.dislikedBy.includes(userId);
            updatedItem.dislikedBy = alreadyDisliked ? updatedItem.dislikedBy.filter(id => id !== userId) : [...updatedItem.dislikedBy, userId];
            updatedItem.likedBy = updatedItem.likedBy.filter(id => id !== userId);
        }
        setLocalNewsItem(updatedItem);

        try {
            const idToken = await firebaseUser.getIdToken(true);
            await axios.post(`https://api-5iacy4tfya-uc.a.run.app/user-news/${localNewsItem.id}/action`,
              { action: action },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
        } catch (error) {
            console.error(`Failed to ${action} post:`, error);
            setLocalNewsItem(originalItem); // Revert state on failure
            alert("Action failed. Please try again.");
        }
    };

    // Save and bookmark logic
    const handleSave = async (e) => {
        e.stopPropagation();
        if (!firebaseUser || isSaving) {
            if (!firebaseUser) alert('Please log in to save posts.');
            return;
        }
        setIsSaving(true);
        const userRef = doc(db, 'users', firebaseUser.uid);
        const newsRef = doc(db, 'userNews', localNewsItem.id);
        try {
            if (hasSaved) {
                await updateDoc(userRef, { savedPosts: arrayRemove(localNewsItem.id) });
                await updateDoc(newsRef, { savedBy: arrayRemove(firebaseUser.uid) });
                setLocalNewsItem(prev => ({ 
                    ...prev, 
                    savedBy: (prev.savedBy || []).filter(id => id !== firebaseUser.uid) 
                }));
            } else {
                await updateDoc(userRef, { savedPosts: arrayUnion(localNewsItem.id) });
                await updateDoc(newsRef, { savedBy: arrayUnion(firebaseUser.uid) });
                setLocalNewsItem(prev => ({ 
                    ...prev, 
                    savedBy: [...(prev.savedBy || []), firebaseUser.uid] 
                }));
            }
        } catch (error) {
            console.error("Error saving post:", error);
        } finally {
            setIsSaving(false);
        }
    };

    // Comment trigger
    const handleCommentClick = (e) => {
        e.stopPropagation();
        if (typeof setActiveCommentPostId === 'function') {
            setActiveCommentPostId(localNewsItem.id);
        } else {
            console.error("setActiveCommentPostId is not a function.");
        }
    };

    // Native Capacitor Sharing implementation matching NewsCard
    const handleShare = async () => {
        const shareUrl = `https://ainewsapp.net/post/${localNewsItem.id}`;
        const author = localNewsItem.userName || localNewsItem.authorName || 'Ai News Reporter';

        const shareData = {
            title: `Ai News: ${localNewsItem.title}`,
            text: `📰 ${localNewsItem.title}\n✍️ Reported By: ${author}\n\nచదవండి Ai News App లో:`,
            url: shareUrl,
            dialogTitle: 'Share this News',
        };

        try {
            await Share.share(shareData);
        } catch (error) {
            console.log("Capacitor share failed, falling back to Web Share API:", error);
            if (navigator.share) {
                try {
                    await navigator.share(shareData);
                } catch (err) {
                    console.error('Web Share API failed:', err);
                    navigator.clipboard.writeText(shareData.url);
                    alert('Link copied to clipboard!');
                }
            } else {
                navigator.clipboard.writeText(shareData.url);
                alert('Link copied to clipboard!');
            }
        }
    };

    const getRelativeTime = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        if (diffInSeconds < 60) return "just now";
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;
        return `${Math.floor(diffInHours / 24)}d ago`;
    };

    return (
        <div className={`wishes-card-wrapper ${isExpanded ? 'expanded' : ''}`}>
            {/* The Image/Video Layer */}
            <div className="wishes-media-container">
                {localNewsItem.videoUrl ? (
                    <div className="wishes-video-wrapper" onClick={(e) => e.stopPropagation()}>
                        <video 
                            ref={videoRef}
                            src={localNewsItem.videoUrl} 
                            muted={isMuted}
                            loop 
                            playsInline 
                            preload={isActive ? "auto" : "none"} // Restricts unviewed slides from fetching heavy streams
                            className="wishes-media-element" 
                        />
                        <button 
                            className="wishes-video-mute-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMuted(!isMuted);
                            }}
                        >
                            <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeHigh} />
                        </button>
                    </div>
                ) : (
                    <img 
                        src={localNewsItem.imageUrls?.[currentImageIndex] || '/placeholder.png'} 
                        alt="" 
                        className="wishes-media-element" 
                        onClick={(e) => e.stopPropagation()}
                        decoding="async" // Decodes the asset without blocking UI swipe rendering
                        loading={isActive ? "eager" : "lazy"} // Loads the active slide image immediately
                    />
                )}
            </div>

            {/* Transparent Text Overlay */}
            <div className="wishes-overlay-glass" onClick={() => isExpanded && setIsExpanded(false)}>
                
                <div className="wishes-info-box">
                    <div className="wishes-arrow-box" onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>
                        <FontAwesomeIcon icon={faChevronUp} className={`wishes-arrow ${isExpanded ? 'active' : ''}`} />
                    </div>

                    <h3 className={`wishes-main-title ${!isExpanded ? 'truncate-1' : ''}`}>
                        {localNewsItem.title}
                    </h3>

                    {/* Content only shows fully when expanded */}
                    <div className={`wishes-desc-scroll ${isExpanded ? 'visible' : 'hidden'}`}>
                        <p>{localNewsItem.description || localNewsItem.content}</p>
                    </div>

                    {/* 💡 REACTION TABS BAR - Visible only when expanded */}
                    {isExpanded && (
                        <div className="wishes-action-tabs" onClick={(e) => e.stopPropagation()}>
                            <button className={`wishes-tab-btn ${hasLiked ? 'active' : ''}`} onClick={() => handleLikeDislike('like')}>
                                <FontAwesomeIcon icon={faThumbsUp} />
                                <span>{(localNewsItem.likedBy || []).length}</span>
                            </button>
                            <button className={`wishes-tab-btn ${hasDisliked ? 'active' : ''}`} onClick={() => handleLikeDislike('dislike')}>
                                <FontAwesomeIcon icon={faThumbsDown} />
                                <span>{(localNewsItem.dislikedBy || []).length}</span>
                            </button>
                            <button className="wishes-tab-btn" onClick={handleCommentClick}>
                                <FontAwesomeIcon icon={faComment} />
                                <span>{localNewsItem.comments?.length || 0}</span>
                            </button>
                            <button className={`wishes-tab-btn ${hasSaved ? 'active' : ''}`} onClick={handleSave} disabled={isSaving}>
                                <FontAwesomeIcon icon={faBookmark} />
                                <span>{hasSaved ? 'Saved' : 'Save'}</span>
                            </button>
                            <button className="wishes-tab-btn" onClick={handleShare}>
                                <FontAwesomeIcon icon={faShareAlt} />
                                <span>Share</span>
                            </button>
                        </div>
                    )}

                    <div className="wishes-bottom-bar">
                        <span className="wishes-user-name">From: {localNewsItem.userName || 'Ai News'}</span>
                        <span className="wishes-timestamp">{getRelativeTime(localNewsItem.timestamp)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WishesCard;